import { environment } from '../../environments/environment';

/**
 * Client half of server-side spatial downsampling (telemetry-pipeline item 3).
 *
 * The server decimates a tick's value array across the SPATIAL axis — never across
 * ticks — emitting the min AND max sample of each bucket in spatial order, so the
 * rendered envelope stays visually lossless. Temporal completeness (the tick set,
 * HWM cursor) is untouched.
 *
 * Wire contract (VericureDataServices, ProjectApiServices.ParseDownsampleRequest):
 *
 *   request  ?resolution=<display points per tick>   <= 0 or absent -> FULL RES
 *            &windowStart=<spatial sample index>     negative/absent -> server default
 *            &windowEnd=<spatial sample index>       negative/absent -> server default
 *
 *   response  DS  : boolean  — true when the arrays are decimated
 *             SIX : number[] — per-row spatial sample index of each value; present
 *                              ONLY when DS is true (non-uniform placement)
 *
 * `windowEnd` is EXCLUSIVE — the server slices [start, end).
 *
 * Sending nothing reproduces today's behaviour exactly, which is why the flag can
 * default off and the server needs no lockstep change.
 */

/** A spatial window in SAMPLE INDICES along the fiber; end is exclusive. */
export interface SpatialWindow {
  start: number;
  end: number;
}

/** Query parameters for a downsampled request. */
export interface DownsampleParams {
  resolution: number;
  windowStart: number;
  windowEnd: number;
}

/**
 * Two points (min + max) are emitted per bucket, so a budget of N display points
 * yields N/2 buckets. Asking for ~2 points per horizontal pixel keeps the envelope
 * at screen fidelity without over-fetching.
 */
export const POINTS_PER_PIXEL = 2;

/** Floor for the point budget, so a tiny/hidden chart never asks for a useless trace. */
export const MIN_RESOLUTION = 256;

/**
 * Ceiling for the point budget. Above roughly this, decimation stops paying for
 * itself relative to the full 22k array and we may as well fetch full resolution.
 */
export const MAX_RESOLUTION = 8192;

/** Client-side enable switch; absent config means off (backward compatible). */
export function downsamplingEnabled(): boolean {
  return (environment as { downsamplingEnabled?: boolean }).downsamplingEnabled === true;
}

/**
 * Display-point budget for a chart of `pixelWidth` CSS pixels.
 *
 * Returns 0 — meaning "send no downsample params, get full resolution" — when the
 * width is unusable (chart not laid out yet, hidden tab, offscreen export surface).
 * That is the safe direction: full-res is always correct, just larger.
 */
export function resolutionForWidth(pixelWidth: number): number {
  if (!Number.isFinite(pixelWidth) || pixelWidth <= 0) {
    return 0;
  }
  const budget = Math.round(pixelWidth * POINTS_PER_PIXEL);
  return Math.min(MAX_RESOLUTION, Math.max(MIN_RESOLUTION, budget));
}

/**
 * Build the request parameters, or null when the request should stay full-res.
 *
 * Returns null when the flag is off, the budget is unusable, or the budget already
 * covers every sample in the window — in that last case the server would return the
 * raw slice anyway, so sending the params only adds query noise.
 */
export function downsampleParamsFor(
  window: SpatialWindow,
  pixelWidth: number
): DownsampleParams | null {
  return downsampleParamsForBudget(window, resolutionForWidth(pixelWidth));
}

/**
 * As `downsampleParamsFor`, but with the point budget supplied rather than derived
 * from the pixel width alone.
 *
 * This is the seam adaptive downsampling injects at: the budget arrives already
 * resolved against throughput, memory and the manual quality override, and
 * everything downstream — including the window-covers-budget shortcut below —
 * behaves identically whatever produced the number.
 */
export function downsampleParamsForBudget(
  window: SpatialWindow,
  resolution: number
): DownsampleParams | null {
  if (!downsamplingEnabled()) {
    return null;
  }
  if (!Number.isFinite(resolution) || resolution <= 0) {
    return null;
  }
  const normalised = normaliseWindow(window);
  const pointsInWindow = normalised.end - normalised.start;
  if (pointsInWindow > 0 && resolution >= pointsInWindow) {
    // Zoomed in past 1:1 — full resolution for this window is the cheaper request.
    return null;
  }
  return {
    resolution,
    windowStart: normalised.start,
    windowEnd: normalised.end,
  };
}

/**
 * Sentinel telling the server "no window — decimate the whole array".
 *
 * ParseDownsampleRequest defaults an unparseable or absent bound to -1, and the
 * decimator treats a negative bound as the full array, so this is the documented
 * way to ask for a point budget without knowing the geometry.
 */
export const WHOLE_ARRAY = -1;

/**
 * Point budget with NO spatial window — for the request that happens before the
 * client knows the geometry.
 *
 * This is the COLD-LOAD case, and it was the expensive hole in item 3. The window
 * comes from LSS/LES, which arrive IN the first response, so on a fresh project
 * the backfill had no window to send; `paramsFor` therefore returned null and the
 * biggest request of the session — every retained tick — went out at FULL
 * RESOLUTION. Downsampling only engaged afterwards, on polls and zooms, which are
 * the cheap requests.
 *
 * The server does not need the window to decimate: `Enabled` keys off
 * `Resolution > 0` alone. So the budget can be sent immediately and the geometry
 * left to the server, which knows the array length anyway.
 */
export function resolutionOnlyParamsFor(
  pixelWidth: number
): DownsampleParams | null {
  return resolutionOnlyParamsForBudget(resolutionForWidth(pixelWidth));
}

/**
 * As `resolutionOnlyParamsFor`, but with the budget supplied rather than derived
 * from pixel width alone — the seam adaptive downsampling injects at.
 *
 * The cold load is the request MOST worth adapting: it is the largest of the
 * session, and it is the one a weak connection or a small device will struggle
 * with. Sizing it by pixels alone would leave exactly that case unhelped.
 */
export function resolutionOnlyParamsForBudget(
  resolution: number
): DownsampleParams | null {
  if (!downsamplingEnabled()) {
    return null;
  }
  if (!Number.isFinite(resolution) || resolution <= 0) {
    return null;
  }
  return {
    resolution,
    windowStart: WHOLE_ARRAY,
    windowEnd: WHOLE_ARRAY,
  };
}

/**
 * Parameters for an explicit full-resolution fetch of a zoomed window.
 *
 * Used by the visibleRangeChanged path: once the user zooms, we re-request that
 * sub-window with a budget >= the samples it contains, so the server returns the
 * raw slice — full-res on demand, without ever fetching the whole trace.
 */
export function fullResolutionParamsFor(
  window: SpatialWindow
): DownsampleParams | null {
  const normalised = normaliseWindow(window);
  const pointsInWindow = normalised.end - normalised.start;
  if (pointsInWindow <= 0) {
    return null;
  }
  return {
    // >= points-in-window makes the server take its full-resolution branch.
    resolution: pointsInWindow,
    windowStart: normalised.start,
    windowEnd: normalised.end,
  };
}

/** Clamp to a sane, ascending, non-negative window. */
export function normaliseWindow(window: SpatialWindow): SpatialWindow {
  const start = Math.max(0, Math.floor(window?.start ?? 0));
  const end = Math.max(start, Math.floor(window?.end ?? start));
  return { start, end };
}

/**
 * Map a visible axis range (metres from the liner start, as SciChart reports it)
 * to spatial SAMPLE INDICES.
 *
 * `lss`/`les` are the payload's window bounds and `linerLength` is CRE - CRB, so
 * `metresPerSample = linerLength / (les - lss)` — the same xFactor the charts
 * already use. Indices are absolute into the row array, i.e. offset by `lss`.
 *
 * Mirror mode: when MIR is set the loss/health charts render the FULL array width
 * and the trace is reversed for display. The underlying sample indices are NOT
 * reversed, so a visible range near the left edge of a mirrored chart corresponds
 * to indices near the END of the array. `mirrored` performs that flip so the
 * requested window matches what the user is actually looking at.
 */
export function visibleRangeToWindow(
  visibleMin: number,
  visibleMax: number,
  opts: {
    lss: number;
    les: number;
    linerLength: number;
    arrayLength?: number;
    mirrored?: boolean;
  }
): SpatialWindow {
  const { lss, les, linerLength } = opts;
  const span = les - lss;
  if (!(span > 0) || !(linerLength > 0)) {
    return { start: lss, end: les };
  }
  const samplesPerMetre = span / linerLength;

  let startIdx = Math.floor(lss + Math.min(visibleMin, visibleMax) * samplesPerMetre);
  let endIdx = Math.ceil(lss + Math.max(visibleMin, visibleMax) * samplesPerMetre);

  if (opts.mirrored) {
    // The mirrored trace is drawn reversed over [lss, upper); flip the window back
    // into un-reversed sample space.
    const upper = opts.arrayLength && opts.arrayLength > lss ? opts.arrayLength : les;
    const flippedStart = lss + (upper - endIdx);
    const flippedEnd = lss + (upper - startIdx);
    startIdx = flippedStart;
    endIdx = flippedEnd;
  }

  const upperBound =
    opts.arrayLength && opts.arrayLength > 0 ? opts.arrayLength : les;
  return normaliseWindow({
    start: Math.max(lss, Math.min(startIdx, upperBound)),
    end: Math.max(lss, Math.min(endIdx, upperBound)),
  });
}

/**
 * X values (metres from the liner start) for one row's values.
 *
 * Downsampled rows carry `SIX`, so points are NON-UNIFORM and each x must come
 * from its true sample index. Full-resolution rows have no `SIX`, and position in
 * the sliced array is the sample index — the existing uniform behaviour.
 *
 * Both branches use the same transform, `(sampleIndex - startDataPoint) * xFactor`,
 * so the axis is identical either way; only where the index comes from differs.
 * (The server documents this as x = index x SamplingInterval; xFactor is that same
 * metres-per-sample figure expressed as linerLength / (les - lss).)
 */
export function xValuesForRow(
  sampleIndices: number[] | undefined,
  valueCount: number,
  startDataPoint: number,
  xFactor: number
): number[] {
  const out = new Array<number>(valueCount);
  if (sampleIndices && sampleIndices.length >= valueCount) {
    for (let k = 0; k < valueCount; k++) {
      out[k] = (sampleIndices[k] - startDataPoint) * xFactor;
    }
    return out;
  }
  for (let k = 0; k < valueCount; k++) {
    out[k] = k * xFactor;
  }
  return out;
}

/** True when this payload's rows carry non-uniform sample indices. */
export function isDownsampled(payload: { DS?: boolean } | null | undefined): boolean {
  return payload?.DS === true;
}

/**
 * Exclusive end of the FIBER SAMPLE range a row covers.
 *
 * `values.length` is not that number whenever `SIX` is present: the array is then
 * a window slice (item 3) or a liner-trimmed response, so its length is the point
 * COUNT, not the sample index it reaches. Anywhere the old code read `LR.length`
 * or `TR.length` as "how far along the fiber this row goes", it needs this instead
 * — most visibly in mirror mode, where the array length defines the fold point and
 * a decimated length folds the trace in the wrong place.
 */
export function sampleSpanEnd(
  values: readonly number[] | null | undefined,
  sampleIndices: readonly number[] | null | undefined,
  fallbackEnd = 0
): number {
  const count = values?.length ?? 0;
  if (sampleIndices && sampleIndices.length > 0) {
    const last = Math.min(count, sampleIndices.length) - 1;
    if (last >= 0) {
      return sampleIndices[last] + 1;
    }
  }
  return count > 0 ? count : fallbackEnd;
}

/**
 * Project one row onto the DENSE liner grid `[start, end)`.
 *
 * This is the single conversion every direct-indexing site needs. A row arrives in
 * one of two shapes and only `SIX` distinguishes them:
 *
 *   SIX absent   the array is the whole fiber; position IS the sample index, so
 *                `values[j]` is correct and this is a plain slice
 *   SIX present  the array is a window slice, decimated and/or liner-TRIMMED, so
 *                `values[j]` is a different sample entirely — or `undefined` once
 *                `j` runs past the shorter array
 *
 * That second case is why this exists. Reading a trimmed row by absolute index
 * does not fail loudly; it silently returns the wrong sample, and past the end it
 * yields `undefined` which the call sites coerce to 0 — a chart of zeros that
 * looks like real telemetry.
 *
 * Collisions REDUCE with `Math.max` by default, matching the zone heatmap: when a
 * cell holds several samples, the hottest one is what must survive. Averaging
 * would hide exactly the peak an operator is looking for.
 */
export function rowOnLinerGrid(
  values: readonly number[] | null | undefined,
  sampleIndices: readonly number[] | null | undefined,
  start: number,
  end: number,
  reduce: (a: number, b: number) => number = Math.max
): number[] {
  const width = Math.max(0, Math.floor(end) - Math.floor(start));
  if (width === 0 || !values || values.length === 0) {
    return new Array<number>(Math.max(0, width)).fill(0);
  }

  if (sampleIndices && sampleIndices.length > 0) {
    // TEMPERATURE, so opt into the leading back-fill: a gap at the front of the
    // liner must not render as 0 C. Every caller of rowOnLinerGrid is a temperature
    // path — the waterfall, the curing chart, the heat-input accumulators and the
    // what-if fold. Zone severity goes through resampleToUniform directly and stays
    // opted out.
    return resampleToUniform(values, sampleIndices, start, end, width, reduce, true);
  }

  // Full array: position is the sample index.
  const out = new Array<number>(width);
  for (let j = 0; j < width; j++) {
    out[j] = values[start + j] ?? 0;
  }
  return out;
}

/**
 * Value at one absolute FIBER SAMPLE INDEX.
 *
 * For the marker series, which plots a single fiber position over time rather than
 * a whole trace. With `SIX` absent the sample index is the array index; with `SIX`
 * present the array is sparse over fiber space, so this finds the nearest carried
 * sample by binary search (SIX is ascending by construction).
 */
export function valueAtSampleIndex(
  values: readonly number[] | null | undefined,
  sampleIndices: readonly number[] | null | undefined,
  sampleIndex: number
): number {
  if (!values || values.length === 0) {
    return 0;
  }
  if (!sampleIndices || sampleIndices.length === 0) {
    return values[sampleIndex] ?? 0;
  }

  const count = Math.min(values.length, sampleIndices.length);
  let lo = 0;
  let hi = count - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (sampleIndices[mid] < sampleIndex) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  // `lo` is the first index at or past the target; the neighbour before it may be
  // closer, so take whichever is nearer rather than always rounding up.
  const after = lo;
  const before = Math.max(0, lo - 1);
  const dAfter = Math.abs(sampleIndices[after] - sampleIndex);
  const dBefore = Math.abs(sampleIndices[before] - sampleIndex);
  return values[dBefore <= dAfter ? before : after] ?? 0;
}

/**
 * Resample a decimated (non-uniform) row back onto a UNIFORM cell grid.
 *
 * Why this exists: heatmaps are drawn with `UniformHeatmapDataSeries`, whose cells
 * are evenly spaced by construction (`xStart`/`xStep`), so a row carrying `SIX`
 * cannot be handed to it directly. SciChart 3.1 does ship
 * `NonUniformHeatmapDataSeries`, but adopting it would mean swapping the
 * renderable-series type, colour map and disposal path on both zone surfaces and
 * the waterfall — churn for no visual gain, because a heatmap's x resolution is
 * already the cell count.
 *
 * Each source point is placed in the cell its true sample index falls in, and
 * collisions REDUCE rather than overwrite. The default reducer is `Math.max`,
 * which is the safety-correct choice for zone severity codes: the worst severity
 * in a cell is what should be shown, never an average that hides it.
 *
 * Cells with no source point inherit the previous cell's value (last-observation
 * carried forward), which keeps the band continuous rather than punching holes.
 */
export function resampleToUniform(
  values: readonly number[],
  sampleIndices: readonly number[] | undefined,
  windowStart: number,
  windowEnd: number,
  cellCount: number,
  reduce: (a: number, b: number) => number = Math.max,
  /**
   * Extend the FIRST observed value back across leading empty cells.
   *
   * OFF BY DEFAULT, AND THAT DEFAULT IS THE WHOLE POINT. This function serves two
   * different kinds of data. For TEMPERATURE a leading gap filled with 0 is a lie —
   * 0 C is a cold reading, and on a -10..180 C colour map it paints solid blue. For
   * ZONE SEVERITY CODES 0 means NO FAULT, and extending the first observed severity
   * backwards would INVENT FAULTS across the whole leading region.
   *
   * So the caller says which it has. rowOnLinerGrid (temperature) opts in; the zone
   * path in temperature.component, which resamples row.ZR, deliberately does not.
   */
  backfillLeading = false
): number[] {
  const cells = Math.max(0, Math.floor(cellCount));
  const out = new Array<number>(cells).fill(0);
  if (cells === 0 || values.length === 0) {
    return out;
  }

  const span = windowEnd - windowStart;
  if (!sampleIndices || sampleIndices.length === 0 || span <= 0) {
    // Already uniform: stretch/truncate positionally.
    for (let c = 0; c < cells; c++) {
      const srcIndex = Math.min(
        values.length - 1,
        Math.floor((c * values.length) / cells)
      );
      out[c] = values[srcIndex];
    }
    return out;
  }

  const filled = new Array<boolean>(cells).fill(false);
  const count = Math.min(values.length, sampleIndices.length);
  for (let k = 0; k < count; k++) {
    const cell = Math.floor(((sampleIndices[k] - windowStart) / span) * cells);
    if (cell < 0 || cell >= cells) {
      continue;
    }
    out[cell] = filled[cell] ? reduce(out[cell], values[k]) : values[k];
    filled[cell] = true;
  }

  // Carry the last observed value forward across empty cells.
  let last = 0;
  let seen = false;
  let firstFilled = -1;
  for (let c = 0; c < cells; c++) {
    if (filled[c]) {
      last = out[c];
      seen = true;
      if (firstFilled < 0) {
        firstFilled = c;
      }
    } else if (seen) {
      out[c] = last;
    }
  }

  // AND BACKWARD, ACROSS THE LEADING CELLS. THE BLUE BAR.
  //
  // The forward carry above only fills cells AFTER the first observed sample —
  // `else if (seen)`. Every cell BEFORE it kept the array's initial 0, and 0 is not
  // "no reading", it is a temperature. On a -10..180 C colour map a 0 lands at
  // offset 0.053, which is essentially pure blue, so a run of leading empty cells
  // paints a SOLID BLUE BAR the full width of the heatmap.
  //
  // Observed on prod2 /graph: a full-width blue bar on the waterfall. It MOVED FROM
  // THE TOP TO THE BOTTOM when the time axis was flipped, which is what identified
  // it — the bar is pinned to the EARLIEST data, not to a screen position, so it is
  // leading cells and not a rendering fault.
  //
  // Leading cells are empty whenever the liner window starts before the first
  // sample index: `windowStart < sampleIndices[0]`. That is padding introduced on
  // READ, not rows that exist in storage, so the fix belongs here.
  //
  // Extending the FIRST observed value backwards is the same convention the forward
  // carry already sets, and it is strictly better than the alternative it replaces:
  // today the gap is filled with an invented 0 C reading, and afterwards it is
  // filled with the nearest real one. NaN would arguably be truer still — a gap is
  // a gap — but it changes how SciChart renders the cell, and that is not a change
  // to make unverified hours before a demo.
  //
  // If NOTHING was observed, firstFilled stays -1 and the row is left as zeros:
  // there is no nearest reading to extend, and that case is genuinely "no data".
  if (backfillLeading && firstFilled > 0) {
    const firstValue = out[firstFilled];
    for (let c = 0; c < firstFilled; c++) {
      out[c] = firstValue;
    }
  }
  return out;
}
